/* ============================================================
   SLIDER ENGINE — shared vanilla JS
   File: assets/slider-engine.js
   Version: 2.0.0

   Usage:
   new SliderEngine(sliderEl, options)

   Options:
   - slidesVisible  {function|number}  slides visible at once
   - loop           {boolean}          infinite loop (default false)
   - dots           {string}           selector for dots container (optional)
   ============================================================ */

class SliderEngine {
  constructor(el, options = {}) {
    this.el      = el;
    this.track   = el.querySelector('[data-slider-track]');
    this.slides  = Array.from(el.querySelectorAll('[data-slider-slide]'));
    this.btnPrev = el.querySelector('[data-slider-prev]');
    this.btnNext = el.querySelector('[data-slider-next]');
    this.dotsEl  = options.dotsEl || null;
    this.loop    = options.loop ?? false;

    this._getVisible = typeof options.slidesVisible === 'function'
      ? options.slidesVisible
      : () => options.slidesVisible ?? 1;

    this.current         = 0;
    this._resizeObserver = null;
    this._touchStartX    = null;
    this._touchStartY    = null;

    this._init();
  }

  get slidesVisible() {
    return this._getVisible();
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - this.slidesVisible);
  }

  _init() {
    this._render();
    this._buildDots();
    this._bindButtons();
    this._bindTouch();
    this._bindResize();
  }

  /* ---------- Render / layout ---------- */
  _render() {
    const visible = this.slidesVisible;
    const gap     = parseFloat(getComputedStyle(this.track).gap) || 0;
    const slideW  = `calc((100% - ${gap * (visible - 1)}px) / ${visible})`;

    this.slides.forEach(slide => {
      slide.style.flex     = `0 0 ${slideW}`;
      slide.style.maxWidth = slideW;
    });

    this.current = Math.min(this.current, this.maxIndex);
    this._moveTo(this.current, false);
    this._updateButtons();
    this._updateDots();
  }

  _moveTo(index, animate = true) {
    this.current = Math.max(0, Math.min(index, this.maxIndex));

    const visible = this.slidesVisible;
    const gap     = parseFloat(getComputedStyle(this.track).gap) || 0;
    const slideW  = (this.track.offsetWidth - gap * (visible - 1)) / visible;
    const offset  = this.current * (slideW + gap);

    this.track.style.transition = animate ? 'transform 0.4s ease' : 'none';
    this.track.style.transform  = `translateX(-${offset}px)`;

    this._updateButtons();
    this._updateDots();

    this.el.dispatchEvent(new CustomEvent('slider:change', {
      bubbles: true,
      detail: { current: this.current, total: this.slides.length }
    }));
  }

  /* ---------- Buttons ---------- */
  _updateButtons() {
    if (!this.btnPrev || !this.btnNext) return;
    if (this.loop) {
      this.btnPrev.disabled = false;
      this.btnNext.disabled = false;
    } else {
      this.btnPrev.disabled = this.current <= 0;
      this.btnNext.disabled = this.current >= this.maxIndex;
    }
  }

  _bindButtons() {
    this.btnPrev?.addEventListener('click', () => this.prev());
    this.btnNext?.addEventListener('click', () => this.next());
  }

  /* ---------- Dots ---------- */
  _buildDots() {
    if (!this.dotsEl) return;
    this.dotsEl.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.className    = 'slider-dots__dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('type', 'button');
      dot.addEventListener('click', () => this._moveTo(i));
      this.dotsEl.appendChild(dot);
    });
    this._updateDots();
  }

  _updateDots() {
    if (!this.dotsEl) return;
    const dots = this.dotsEl.querySelectorAll('.slider-dots__dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('slider-dots__dot--active', i === this.current);
      dot.setAttribute('aria-current', i === this.current ? 'true' : 'false');
    });
  }

  /* ---------- Touch / swipe ---------- */
  _bindTouch() {
    this.track.addEventListener('touchstart', (e) => {
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
    }, { passive: true });

    this.track.addEventListener('touchend', (e) => {
      if (this._touchStartX === null) return;
      const dx = e.changedTouches[0].clientX - this._touchStartX;
      const dy = e.changedTouches[0].clientY - this._touchStartY;

      // Only handle horizontal swipes (ignore vertical scroll)
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx < 0 ? this.next() : this.prev();
      }
      this._touchStartX = null;
      this._touchStartY = null;
    }, { passive: true });
  }

  /* ---------- Resize ---------- */
  _bindResize() {
    this._resizeObserver = new ResizeObserver(() => this._render());
    this._resizeObserver.observe(this.el);
  }

  /* ---------- Public API ---------- */
  prev() {
    if (this.loop && this.current <= 0) {
      this._moveTo(this.maxIndex);
    } else {
      this._moveTo(this.current - 1);
    }
  }

  next() {
    if (this.loop && this.current >= this.maxIndex) {
      this._moveTo(0);
    } else {
      this._moveTo(this.current + 1);
    }
  }

  destroy() {
    this._resizeObserver?.disconnect();
  }
}

window.SliderEngine = SliderEngine;
